import { IconGitBranch, Logo } from '@developerz/ui';
import {
  createEffect,
  createSignal,
  type JSX,
  Match,
  on,
  onCleanup,
  onMount,
  Show,
  Switch,
} from 'solid-js';
import { SsoEmailForm } from '../components/SsoEmailForm';
import { SsoOrgForm } from '../components/SsoOrgForm';
import { Button, buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { API_BASE_URL } from '../lib/api';
import { LOGIN_ERROR_PARAM, loginErrorMessage } from '../lib/login-error';
import { currentReturnTo, withReturnTo } from '../lib/return-to';

// Full-screen sign-in, shown by <App/> when `useMe` reports no session — at ANY
// path (#475). One method per screen: the chooser offers three legs (GitHub,
// org-handle BYO IdP, work email); a pick swaps the card to that form with a back
// link. Legs set the httpOnly `dz_session` cookie; `?login_error` → retry alert.

type Method = 'choose' | 'org' | 'email';

/** Where an identity with no account goes to ask for one (`apps/marketing` `/waitlist`). */
const WAITLIST_URL = 'https://www.developerz.ai/waitlist';

/** #753: each leg is deep-linkable — the path names the view. */
export function methodFromPath(pathname: string): Method {
  if (pathname === '/login/email') return 'email';
  if (pathname === '/login/sso') return 'org';
  return 'choose';
}

/** The canonical path for a view (chooser lives at /login). */
function methodPath(m: Method): string {
  return m === 'email' ? '/login/email' : m === 'org' ? '/login/sso' : '/login';
}

export function Login(): JSX.Element {
  const [method, setMethod] = createSignal<Method>(
    typeof window === 'undefined' ? 'choose' : methodFromPath(window.location.pathname),
  );

  const query = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  // Optional ?org=<slug> prefill for the SSO leg — validated against the same
  // GitHub-login shape the form's pattern enforces; anything else is ignored.
  const orgParam = query.get('org');
  const orgPrefill = orgParam && /^[A-Za-z0-9-]{1,39}$/.test(orgParam) ? orgParam : null;

  // Leg switches are ROUTE navigation (search preserved); Login renders outside
  // <Router>, so this speaks history directly and popstate drives back/forward.
  const navigate = (m: Method): void => {
    window.history.pushState({}, '', methodPath(m) + window.location.search);
    setMethod(m);
  };
  onMount(() => {
    const onPop = (): void => void setMethod(methodFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    onCleanup(() => window.removeEventListener('popstate', onPop));
  });
  // Where a finished sign-in lands, resolved ONCE: picking a method pushState's
  // the path to `/login/email`, so a leg that read the location for itself would
  // see the chooser and drop the invitee's `?token=` (arc 101 slice 04).
  const returnTo = typeof window === 'undefined' ? null : currentReturnTo(window.location);
  // The code's sentence is `lib/login-error.ts`'s, shared with the shell's
  // <LoginErrorNotice/> for a signed-in landing.
  const errorCode = query.get(LOGIN_ERROR_PARAM);
  const errorText = errorCode ? loginErrorMessage(errorCode) : null;

  // `?reason=no_account` (arc 101 slice 01): the work-email SSO leg verified an
  // identity that has no `users` row, and the API bounced it here rather than
  // silently. Not an error — the person did nothing wrong — so it is a status
  // line with the one next move, the waitlist. Presence via `t.has`, like the
  // error codes: an unknown reason renders nothing rather than `⟦key⟧`.
  const reason = query.get('reason');
  const reasonKey =
    reason !== null && t.has(`login.reason.${reason}`) ? `login.reason.${reason}` : null;

  // A view swap unmounts the control that held focus — land keyboard/SR users on
  // the new view's input (or first action); deferred so mount never steals focus.
  let viewRef: HTMLDivElement | undefined;
  createEffect(
    on(
      method,
      () => {
        queueMicrotask(() => {
          const target =
            viewRef?.querySelector<HTMLElement>('input') ??
            viewRef?.querySelector<HTMLElement>('a, button');
          target?.focus();
        });
      },
      { defer: true },
    ),
  );

  return (
    <main class="login">
      <div class="card card--footered login__card">
        <header class="card__header login__header">
          <span class="login__logo" aria-hidden="true">
            <Logo variant="icon" height={36} />
          </span>
          <h1 class="card__title login__title">
            {t('login.title_pre')} <strong>developerz.ai</strong>
          </h1>
          <p class="card__desc">{t('login.subtitle')}</p>
        </header>
        <div class="card__content login__content">
          <Show when={errorText}>
            <div class="alert alert--destructive" role="alert">
              <p class="alert__title">{errorText}</p>
            </div>
          </Show>
          <Show when={reasonKey} keyed>
            {(key) => (
              <div class="alert" role="status">
                <p class="alert__title">{t(key)}</p>
                <a class={buttonClass({ variant: 'link' })} href={WAITLIST_URL}>
                  {t('login.no_account_cta')}
                </a>
              </div>
            )}
          </Show>
          <Switch>
            <Match when={method() === 'choose'}>
              <div class="login__view" ref={viewRef}>
                <a
                  class={buttonClass()}
                  href={withReturnTo(`${API_BASE_URL}/auth/github/login`, returnTo)}
                >
                  <IconGitBranch />
                  {t('login.cta')}
                </a>
                <p class="login__divider" aria-hidden="true">
                  {t('login.or')}
                </p>
                <Button variant="outline" onClick={() => navigate('org')}>
                  {t('login.method_org')}
                </Button>
                <Button variant="outline" onClick={() => navigate('email')}>
                  {t('login.method_email')}
                </Button>
                <p class="login__note">{t('login.scopes_note')}</p>
              </div>
            </Match>
            <Match when={method() !== 'choose'}>
              <div class="login__view" ref={viewRef}>
                <button
                  type="button"
                  class={`${buttonClass({ variant: 'link' })} login__back`}
                  onClick={() => navigate('choose')}
                >
                  {t('login.back')}
                </button>
                <Show when={method() === 'org'} fallback={<SsoEmailForm returnTo={returnTo} />}>
                  <SsoOrgForm initialOrg={orgPrefill} returnTo={returnTo} />
                </Show>
              </div>
            </Match>
          </Switch>
        </div>
        <footer class="card__footer login__footer">
          <a class={buttonClass({ variant: 'link' })} href={WAITLIST_URL}>
            {t('login.no_account_cta')}
          </a>
        </footer>
      </div>
    </main>
  );
}
